import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, ExternalLink, LogOut, Users, MapPin } from 'lucide-react';
import { startImpersonating } from '@/lib/axiosClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const FarmManagerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [farms, setFarms] = useState(user?.farms || []);
  const [loading, setLoading] = useState(!user?.farms);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/auth/me`, { headers: auth() });
      setFarms(data.farms || []);
    } catch (e) {
      toast.error('Failed to load assigned farms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.farms || user.farms.length === 0) load();
  }, [load, user]);

  const openFarm = (farm) => {
    if (farm.status === 'disabled') {
      toast.error(`Farm "${farm.name}" is disabled. Contact platform admin.`);
      return;
    }
    // Reuse the impersonation mechanism: store active farm; axios interceptor sends X-Farm-Id
    startImpersonating(farm.id, farm.name);
    navigate('/grower');
    setTimeout(() => window.location.reload(), 50);
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-7 h-7 text-[#1A4D2E]" />
          <div>
            <h1 className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A3ROS — Farm Manager
            </h1>
            <p className="text-xs text-gray-500">
              Welcome <strong>{user?.full_name || user?.username}</strong> · managing {farms.length} farm(s)
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1A4D2E]" />
              Your Farms
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Pick a farm below to open it. You&apos;ll see and operate it with Grower-level access.
            </p>
          </CardHeader>
          <CardContent>
            {loading && <p className="text-gray-500">Loading…</p>}
            {!loading && farms.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                <p>You have no farms assigned yet.</p>
                <p className="text-xs mt-1">Contact your platform admin to be added to a farm.</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {farms.map((f) => (
                <div
                  key={f.id}
                  className="border border-gray-200 rounded-xl p-5 bg-white hover:shadow-md transition-shadow"
                  data-testid={`farm-card-${f.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{f.name}</h3>
                      {f.location && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />{f.location}
                        </p>
                      )}
                    </div>
                    <Badge className={f.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}>
                      {f.status}
                    </Badge>
                  </div>
                  <Button
                    onClick={() => openFarm(f)}
                    className="w-full bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                    disabled={f.status === 'disabled'}
                    data-testid={`open-farm-${f.id}`}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Open Farm
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FarmManagerDashboard;
